import { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Sun, 
  Moon, 
  Bell, 
  Settings, 
  User,
  RefreshCw,
  Plus,
  ChevronDown,
  Clock,
  Calendar,
  LogOut,
  Shield,
  Users,
  X,
  AlertTriangle,
  Info,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export const Header = ({ 
  tenantName, 
  lastRefresh, 
  onRefresh, 
  isRefreshing,
  onOpenWizard,
  timeRange,
  onTimeRangeChange,
  onShowAdmin,
  onOpenSettings,
  notifications: externalNotifications,
  onDismissNotification,
  onNotificationClick,
}) => {
  const { theme, toggleTheme } = useTheme();
  const { user, isAdmin, isProjectManager, logout } = useAuth();
  
  // Internal notification state for dismissals
  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      const stored = localStorage.getItem('dismissedNotifications');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Default notifications if none provided
  const defaultNotifications = [
    {
      id: 'service-degraded',
      title: 'Power BI Service Degraded',
      time: '2 hours ago',
      type: 'warning',
      section: 'm365',
    },
    {
      id: 'secrets-expiring',
      title: '3 Secrets expiring in 30 days',
      time: 'Today',
      type: 'warning',
      section: 'security',
    },
    {
      id: 'patch-compliance',
      title: 'Patch compliance below 95%',
      time: 'Yesterday',
      type: 'info',
      section: 'operations',
    },
  ];

  const notifications = externalNotifications || defaultNotifications;
  const visibleNotifications = notifications.filter(n => !dismissedIds.includes(n.id));

  const handleDismiss = (e, notificationId) => {
    e.stopPropagation();
    const newDismissed = [...dismissedIds, notificationId];
    setDismissedIds(newDismissed);
    localStorage.setItem('dismissedNotifications', JSON.stringify(newDismissed));
    if (onDismissNotification) {
      onDismissNotification(notificationId);
    }
  };

  const handleNotificationClick = (notification) => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
  };

  const clearAllNotifications = () => {
    const allIds = notifications.map(n => n.id);
    setDismissedIds(allIds);
    localStorage.setItem('dismissedNotifications', JSON.stringify(allIds));
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'success':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/50 bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center justify-between px-6 lg:px-8">
        {/* Left Side */}
        <div className="flex items-center gap-4">
          <div>
            <h1 
              className="text-xl font-bold tracking-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="header-title"
            >
              Executive Dashboards
            </h1>
            {tenantName && (
              <p className="text-sm text-muted-foreground">
                {tenantName}
              </p>
            )}
          </div>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-3">
          {/* Time Range Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="time-range-dropdown">
                <Calendar className="w-4 h-4" />
                {timeRange || '30 days'}
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onTimeRangeChange?.('7 days')} data-testid="time-7d">
                Last 7 days
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTimeRangeChange?.('30 days')} data-testid="time-30d">
                Last 30 days
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTimeRangeChange?.('90 days')} data-testid="time-90d">
                Last 90 days
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Last Refresh */}
          {lastRefresh && (
            <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>Updated {lastRefresh}</span>
            </div>
          )}

          {/* Refresh Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={onRefresh}
            disabled={isRefreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>

          {/* Create Metric Button */}
          <Button
            onClick={onOpenWizard}
            size="sm"
            className="gap-2"
            data-testid="create-metric-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">Create Metric</span>
          </Button>

          {/* Admin Button - For admins and project managers */}
          {(isAdmin || isProjectManager) && onShowAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={onShowAdmin}
              className="gap-2"
              data-testid="admin-btn"
            >
              <Users className="w-4 h-4" />
              <span className="hidden md:inline">{isAdmin ? 'Admin' : 'Projects'}</span>
            </Button>
          )}

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-4 h-4" />
                {visibleNotifications.length > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-[10px] bg-destructive text-destructive-foreground">
                    {visibleNotifications.length}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-2 py-1.5">
                <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
                {visibleNotifications.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-auto py-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={clearAllNotifications}
                  >
                    Clear all
                  </Button>
                )}
              </div>
              <DropdownMenuSeparator />
              {visibleNotifications.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500/50" />
                  No notifications
                </div>
              ) : (
                visibleNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="flex items-start gap-3 px-2 py-2 hover:bg-accent rounded-md cursor-pointer group"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight">{notification.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{notification.time}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      onClick={(e) => handleDismiss(e, notification.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme Toggle */}
          <Button
            variant="outline"
            size="icon"
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
          >
            {theme === 'dark' ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" data-testid="user-menu-btn">
                <User className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.name || 'User'}</p>
                  <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
                  {isAdmin && (
                    <Badge variant="outline" className="mt-1 text-xs">
                      <Shield className="w-3 h-3 mr-1" />
                      Admin
                    </Badge>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onOpenSettings} data-testid="settings-menu-item">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              {(isAdmin || isProjectManager) && onShowAdmin && (
                <DropdownMenuItem onClick={onShowAdmin}>
                  <Users className="w-4 h-4 mr-2" />
                  {isAdmin ? 'User Management' : 'Project Management'}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
